import { useMemo } from "react";

import { useGanttChartData } from "../../../../../reactQuery/epics";
import { useCustomSearchParams } from "../../../../../hooks/useCustomSearchParams";

const taskColors = [
  "#4285F4",
  "#EA4335",
  "#FBBC05",
  "#34A853",
  "#8E24AA",
  "#16A2D7",
  "#FF6D00",
  "#00897B",
  "#C0CA33",
  "#D81B60",
];

export function useGanttChartState() {
  const [milestone] = useCustomSearchParams("milestone");
  const { ganttChartData, isLoading, isError } = useGanttChartData(milestone);
  const ganttData = ganttChartData?.data?.tasks || [];
  const maxVisibleDays = 7;

  const { maxDate, minDate, totalDays } = useMemo(() => {
    if (!ganttData.length) {
      const now = new Date();
      return { maxDate: now, minDate: now, totalDays: 0 };
    }

    const dates = ganttData.reduce(
      (accumulator, task) => {
        const startDate = new Date(task.start_date);
        const endDate = new Date(task.end_date);

        return {
          minDate: startDate < accumulator.minDate ? startDate : accumulator.minDate,
          maxDate: endDate > accumulator.maxDate ? endDate : accumulator.maxDate,
        };
      },
      {
        minDate: new Date(ganttData[0].start_date),
        maxDate: new Date(ganttData[0].end_date),
      }
    );

    return {
      ...dates,
      totalDays: Math.ceil(
        (dates.maxDate - dates.minDate) / (1000 * 60 * 60 * 24)
      ),
    };
  }, [ganttData]);

  const userGroups = useMemo(() => {
    const groups = {};

    ganttData.forEach((task) => {
      if (task.duration_days < 1) {
        return;
      }

      if (!groups[task.user_id]) {
        groups[task.user_id] = {
          tasks: [],
          userName: task.user_name,
        };
      }

      groups[task.user_id].tasks.push(task);
    });

    return Object.values(groups);
  }, [ganttData]);

  const calculateTaskPosition = useMemo(
    () => (task, index) => {
      const workHoursPerDay = 8;
      const startDate = new Date(task.start_date);
      const endDate = new Date(task.end_date);
      const startDayIndex = Math.floor(
        (startDate - minDate) / (1000 * 60 * 60 * 24)
      );
      const endDayIndex = Math.floor(
        (endDate - minDate) / (1000 * 60 * 60 * 24)
      );
      const startHour = Math.min(Math.max(startDate.getHours(), 0), workHoursPerDay);
      const endHour = Math.min(Math.max(endDate.getHours(), 0), workHoursPerDay);
      const totalWorkHours = totalDays * workHoursPerDay || workHoursPerDay;
      const startPercentage =
        ((startDayIndex * workHoursPerDay + startHour) / totalWorkHours) * 100;
      const widthPercentage =
        (((endDayIndex * workHoursPerDay + endHour) -
          (startDayIndex * workHoursPerDay + startHour)) /
          totalWorkHours) *
          100 -
        0.08;

      return {
        backgroundColor: taskColors[index % taskColors.length],
        left: `${startPercentage}%`,
        position: "absolute",
        width: `${widthPercentage}%`,
      };
    },
    [minDate, totalDays]
  );

  return {
    calculateTaskPosition,
    ganttData,
    isError,
    isLoading,
    maxVisibleDays,
    minDate,
    totalDays,
    userGroups,
  };
}
